import {
  Card,
  Cascader,
  DatePicker,
  Select,
  Table,
  Tabs,
  Typography,
} from 'antd';
import moment, { type Moment } from 'moment';
import type { FC } from 'react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import PageHeader from '@/components/PageHeader';
import useService from '@/lib/hooks/useService';
import type { RootState } from '@/redux/store';
import { feedbackSchema } from '@/types/database/feedback';
import { generateHouseHoldNumberCascader } from '@/utils/generateHouseHoldNumberCascader';

import Detail from './Detail';
import { getColumns, getDisplayFeedbackData, messagesFetcher } from './helpers';
import styles from './index.module.scss';
import type { FeedbackQuery, TableData } from './types';

const FeedbackSchedule: FC = () => {
  const [tabKey, setTabKey] = useState<'已讀' | '未讀'>('未讀');
  const [houseNumber, setHouseNumber] = useState<(string | number)[]>();
  const [type, setType] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[Moment, Moment] | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<TableData | null>(
    null
  );
  const [residents] = useService({
    collectionName: 'residentAccountNumbers',
    query: {
      $select: ['householdNumber'],
    },
  });
  const [feedbackTypes] = useService({
    collectionName: 'categoryMenu',
    query: {
      name: '意見反應',
    },
  });

  const feedbackQuery: FeedbackQuery = {};

  if (houseNumber) {
    feedbackQuery.householdNumber = houseNumber.join('-');
  }
  if (type) {
    feedbackQuery.type = type;
  }
  if (dateRange) {
    feedbackQuery.createdTime = {
      $gte: dateRange[0].valueOf(),
      $lte: dateRange[1].valueOf(),
    };
  }

  const [feedbacks] = useService({
    collectionName: 'feedback',
    query: feedbackQuery,
    dataSchema: feedbackSchema,
  });

  const user = useSelector((state: RootState) => state.user);

  const { data: lastMessages } = useSWR(
    '/ezcon/api/postMongoAggregate',
    messagesFetcher,
    {
      refreshInterval: 1000 * 3,
    }
  );

  const houseOptions = generateHouseHoldNumberCascader(
    residents.map(({ householdNumber }) => householdNumber)
  );

  const typeOptions = feedbackTypes[0]?.data.map((name: string) => ({
    label: name,
    value: name,
  }));

  const checkoutFeedback = (feedback: TableData) => {
    setSelectedFeedback(feedback);
    setShowDetail(true);
  };

  const columns = getColumns(checkoutFeedback);

  const filteredData = getDisplayFeedbackData({
    feedbacks,
    lastMessages: lastMessages || [],
    userId: user?.auth?.user?._id,
    tabKey,
  });

  return (
    <div>
      <PageHeader
        title="回覆進度"
        breadcrumbs={[{ name: '意見反應' }, { name: '回覆進度' }]}
      />
      <div className={styles.filters}>
        <div>
          <Typography.Text>戶號：</Typography.Text>
          <Cascader
            options={houseOptions}
            value={houseNumber}
            onChange={(val) => setHouseNumber(val)}
            placeholder="請選擇戶號"
          />
        </div>
        <div>
          <Typography.Text>類別：</Typography.Text>
          <Select
            options={typeOptions}
            placeholder="請選擇類別"
            allowClear
            value={type}
            onChange={(val) => setType(val)}
          />
        </div>
        <div>
          <Typography.Text>日期：</Typography.Text>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(val) => {
              if (val) {
                return setDateRange([moment(val[0]), moment(val[1])]);
              }
              return setDateRange(null);
            }}
          />
        </div>
      </div>
      <Tabs
        items={[
          {
            label: (
              <Typography.Text className={styles.tabLabel}>
                未讀
              </Typography.Text>
            ),
            tabKey: '未讀',
            key: '未讀',
          },
          {
            label: (
              <Typography.Text className={styles.tabLabel}>
                已讀
              </Typography.Text>
            ),
            tabKey: '已讀',
            key: '已讀',
          },
        ]}
        tabBarStyle={{ margin: 0 }}
        size="middle"
        activeKey={tabKey}
        onChange={(key) => setTabKey(key === '未讀' ? '未讀' : '已讀')}
      />
      <Card>
        <Table bordered columns={columns} dataSource={filteredData} />
      </Card>
      <Detail
        open={showDetail}
        onClose={() => {
          setShowDetail(false);
          setSelectedFeedback(null);
        }}
        feedback={selectedFeedback}
        user={{ id: user?.auth?.user?._id, name: user?.auth?.user?.name }}
      />
    </div>
  );
};

export default FeedbackSchedule;
